import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, CheckCircle, X, Globe, Search, MapPin, Star, Target, Zap, TrendingUp, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getStoredUtm, getStoredVisitorId } from '../hooks/useVisitTracker';

/* ── Branches met iconen ─────────────────────────────────────────────── */
const BRANCHES = [
  { id: 'horeca', label: 'Horeca & Retail', icon: '🍽️' },
  { id: 'bouw', label: 'Bouw & Installatie', icon: '🔨' },
  { id: 'zorg', label: 'Zorg & Welzijn', icon: '🏥' },
  { id: 'zakelijk', label: 'Zakelijke Diensten', icon: '💼' },
  { id: 'tech', label: 'Tech & IT', icon: '💻' },
  { id: 'ecommerce', label: 'E-commerce', icon: '🛒' },
  { id: 'vastgoed', label: 'Vastgoed', icon: '🏢' },
  { id: 'onderwijs', label: 'Onderwijs', icon: '📚' },
  { id: 'automotive', label: 'Automotive', icon: '🚗' },
  { id: 'financieel', label: 'Financiën', icon: '💰' },
  { id: 'beauty', label: 'Beauty & Wellness', icon: '💆' },
  { id: 'overig', label: 'Overig', icon: '✨' },
];

/* ── Groeidoelen ─────────────────────────────────────────────────────── */
const DOELEN = [
  { id: 'leads', label: 'Meer leads genereren', icon: Zap, color: '#8b5cf6' },
  { id: 'seo', label: 'Beter gevonden in Google', icon: Search, color: '#059669' },
  { id: 'lokaal', label: 'Lokale zichtbaarheid', icon: MapPin, color: '#D4A017' },
  { id: 'reputatie', label: 'Reviews verbeteren', icon: Star, color: '#f59e0b' },
  { id: 'website', label: 'Website verbeteren', icon: Globe, color: '#3b82f6' },
  { id: 'ads', label: 'Meer uit advertenties', icon: Target, color: '#ef4444' },
  { id: 'social', label: 'Social media groei', icon: Share2, color: '#0EA5E9' },
  { id: 'alles', label: 'Alles verbeteren', icon: TrendingUp, color: '#D4A017' },
];

/* ── Step indicator ──────────────────────────────────────────────────── */
const STEPS = [
  { n: 1, label: 'Uw website' },
  { n: 2, label: 'Branche' },
  { n: 3, label: 'Locatie' },
  { n: 4, label: 'Doelen' },
  { n: 5, label: 'Analyse' },
  { n: 6, label: 'Concurrenten' },
];

/* ── Left panel content per stap ─────────────────────────────────────── */
const LEFT_CONTENT = {
  1: { emoji: '🌐', title: 'We analyseren uw website', desc: 'Door uw website in te voeren kunnen wij een grondige technische analyse uitvoeren — gratis en binnen 2 minuten klaar.' },
  2: { emoji: '🎯', title: 'Branche-specifieke inzichten', desc: 'Elke branche heeft andere uitdagingen en kansen. We passen uw analyse aan op basis van uw sector.' },
  3: { emoji: '📍', title: 'Lokale zichtbaarheid', desc: 'Voor de meeste bedrijven is lokale vindbaarheid de grootste kans. We analyseren uw regio specifiek.' },
  4: { emoji: '🚀', title: 'Uw persoonlijke groeiplan', desc: 'Op basis van uw doelen maken wij een geprioriteerde lijst van verbeteringen die het meeste impact hebben.' },
  5: { emoji: '⚙️', title: 'Uw analyse loopt...', desc: 'We scannen uw website, analyseren uw online aanwezigheid en vinden uw concurrenten automatisch.' },
  6: { emoji: '🏢', title: 'Uw concurrenten', desc: 'We hebben automatisch uw directe concurrenten gevonden. Controleer of dit klopt en ga naar uw dashboard.' },
};

/* ── Scan stappen teksten ────────────────────────────────────────────── */
const SCAN_STEPS = [
  { text: 'Website bereikbaarheid controleren', icon: Globe },
  { text: 'Technische SEO analyseren', icon: Search },
  { text: 'Lokale vindbaarheid beoordelen', icon: MapPin },
  { text: 'Online reputatie analyseren', icon: Star },
  { text: 'Groeikansen identificeren', icon: TrendingUp },
  { text: 'Persoonlijk dashboard bouwen', icon: Zap },
];

/* ════════════════════════════════════════════════════════════════════ */
export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [scanStep, setScanStep] = useState(0);
  const [scanProgress, setScanProgress] = useState(0);
  const [form, setForm] = useState({
    company_name: '', website_url: '',
    branche: '', branche_label: '',
    locatie: '', service_type: '',
    heeft_google_profiel: null, heeft_social_media: null,
    heeft_analytics: null, heeft_advertenties: null,
    groeidoelen: [],
  });
  const [urlChecking, setUrlChecking] = useState(false);
  const [urlStatus, setUrlStatus] = useState(null); // 'ok' | 'warn' | null
  const urlTimeout = useRef(null);
  const [foundConcurrenten, setFoundConcurrenten] = useState([]);

  useEffect(() => {
    if (user?.website_url) setForm(current => ({ ...current, website_url: current.website_url || user.website_url }));
  }, [user?.website_url]);

  // Check URL in real-time
  useEffect(() => {
    const raw = form.website_url.trim();
    if (!raw || raw.length < 4) { setUrlStatus(null); return; }
    clearTimeout(urlTimeout.current);
    urlTimeout.current = setTimeout(async () => {
      setUrlChecking(true);
      try {
        let url = raw;
        if (!url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;
        const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/check-website`, { url });
        setUrlStatus(r.data.reachable ? 'ok' : 'fail');
      } catch {
        setUrlStatus('fail');
      } finally {
        setUrlChecking(false);
      }
    }, 800);
  }, [form.website_url]);

  const token = localStorage.getItem('portal_token');

  // Als niet ingelogd, redirect naar register
  useEffect(() => {
    if (!token) navigate('/register');
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleDoel = (id) => set('groeidoelen', form.groeidoelen.includes(id) ? form.groeidoelen.filter(g => g !== id) : [...form.groeidoelen, id]);

  const canNext = () => {
    if (step === 1) return form.website_url.trim().length > 3 && form.company_name.trim().length > 1;
    if (step === 2) return !!form.branche;
    if (step === 3) return form.locatie.trim().length > 2 && !!form.service_type;
    if (step === 4) return form.groeidoelen.length > 0;
    return true;
  };

  const startAnalyse = async () => {
    setStep(5);

    setScanStep(0);
    setScanProgress(0);

    // Start scan async — backend verwerkt op achtergrond
    try {
      const payload = {
        company_name: form.company_name,
        website_url: form.website_url,
        branche: form.branche_label || form.branche,
        locatie: form.locatie,
        service_type: form.service_type,
        heeft_google_profiel: form.heeft_google_profiel || false,
        heeft_social_media: form.heeft_social_media || false,
        heeft_analytics: form.heeft_analytics || false,
        heeft_advertenties: form.heeft_advertenties || false,
        groeidoelen: form.groeidoelen,
        concurrenten: [],
        naam: '',
        email: '',
        wachtwoord: 'x',
        visitor_id: getStoredVisitorId(),
        utm: getStoredUtm(),
      };

      const startRes = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/radar/analyse`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });

      const jobId = startRes.data.job_id;
      if (!jobId) throw new Error('Geen job_id ontvangen');
      setScanProgress(10);

      // Poll status tot voltooid
      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const statusRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/radar/analyse/status/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000,
          });
          const job = statusRes.data;
          if (job.progress === 'AI analyse uitvoeren...') { setScanStep(1); setScanProgress(35); }
          if (job.progress === 'Opdrachten genereren...') { setScanStep(4); setScanProgress(80); }

          if (job.status === 'completed' && job.result) {
            setScanProgress(100);
            setScanStep(SCAN_STEPS.length);
            if (job.result.token) {
              localStorage.setItem('portal_token', job.result.token);
            }
            if (job.result.concurrenten && job.result.concurrenten.length > 0) {
              setFoundConcurrenten(job.result.concurrenten);
              setStep(6);
            } else {
              await refreshUser();
              navigate('/platform');
            }
            return;
          }
          if (job.status === 'failed') {
            const failure = new Error(job.error || 'Analyse mislukt');
            failure.terminal = true;
            throw failure;
          }
        } catch (pollErr) {
          if (pollErr.terminal || [401, 403].includes(pollErr.response?.status)) throw pollErr;
          if (pollErr.response?.status === 404) continue;
          if (attempts > 10) throw pollErr;
        }
      }
      throw new Error('Analyse duurt te lang');

    } catch (err) {
      const errMsg = err.response?.data?.detail || err.message || '';
      if (errMsg.toLowerCase().includes('website') || !form.website_url?.trim()) {
        toast.error('Geen website gevonden. Voeg uw website toe via Instellingen > Bedrijfsinformatie.');
      } else {
        toast.error('Analyse mislukt. Probeer het opnieuw of neem contact op via info@imperiaai.nl');
      }
      setScanProgress(0);
      setScanStep(0);
      setStep(4);
    }
  };

  const confirmConcurrenten = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/platform/concurrenten/update`, {
        concurrenten: foundConcurrenten,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      // Silently continue
    }
    await refreshUser();
    navigate('/platform');
  };

  const left = LEFT_CONTENT[step];

  const pageVariants = {
    initial: { opacity: 0, x: 30 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -30 },
  };

  const YNCard = ({ label, value, field, yes = true }) => (
    <button data-testid={`onboarding-${field}-${yes ? 'yes' : 'no'}`} type="button" onClick={() => set(field, yes)}
      style={{
        flex: 1, padding: '20px 16px', borderRadius: '16px', border: `2px solid ${form[field] === yes ? '#D4A017' : '#e2e8f0'}`,
        background: form[field] === yes ? '#D4A017' : 'white', cursor: 'pointer', transition: 'all 0.2s',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
      }}>
      <span style={{ fontSize: '28px' }}>{yes ? '✅' : '❌'}</span>
      <span style={{ fontWeight: 700, fontSize: '15px', color: form[field] === yes ? '#0f172a' : '#374151' }}>{label}</span>
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'Inter', sans-serif", position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #D4A017, #f5c842, #D4A017)', zIndex: 100 }} />

      {/* Left panel */}
      <div className="hidden lg:flex lg:w-5/12 flex-col"
        style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)', position: 'sticky', top: 0, height: '100vh' }}>
        <div style={{ position: 'absolute', bottom: '20%', right: 0, width: '350px', height: '350px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,160,23,0.1), transparent)', filter: 'blur(60px)', pointerEvents: 'none' }} />

        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '48px', position: 'relative', zIndex: 10 }}>
          {/* Logo */}
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', textDecoration: 'none', marginBottom: 'auto' }}>
            <div style={{ width: '32px', height: '32px', background: '#D4A017', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#0f172a', fontWeight: 800, fontSize: '16px' }}>♛</span>
            </div>
            <span style={{ fontFamily: "'Sora', sans-serif", color: 'white', fontWeight: 700, fontSize: '16px' }}>IMPERIA AI</span>
          </Link>

          {/* Dynamic content per step */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.45 }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>{left?.emoji}</div>
                <h2 style={{ fontFamily: "'Sora', sans-serif", color: 'white', fontSize: '24px', fontWeight: 800, lineHeight: 1.25, marginBottom: '14px', letterSpacing: '-0.02em' }}>
                  {left?.title}
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', lineHeight: 1.7 }}>
                  {left?.desc}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '40px' }}>
            {[['238+', 'Bedrijven'], ['€18M+', 'Omzet'], ['4.8★', 'Beoordeling'], ['Gratis', 'Account']].map(([v, l]) => (
              <div key={l} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '10px', padding: '12px' }}>
                <p style={{ fontFamily: "'Sora', sans-serif", color: '#D4A017', fontWeight: 800, fontSize: '16px', margin: 0 }}>{v}</p>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginTop: '2px' }}>{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Form */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f8fafc', overflowY: 'auto' }}>
        {/* Top: Steps */}
        {step < 5 && step !== 6 && (
          <div className="onboard-top-bar" style={{ padding: '20px 32px', background: 'white', borderBottom: '1px solid #f1f5f9' }}>
            {/* Mobile logo */}
            <div className="lg:hidden mb-4 flex items-center gap-2">
              <Link to="/" className="flex items-center gap-2">
                <div style={{ width: '28px', height: '28px', background: '#0f172a', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ color: '#D4A017', fontSize: '13px' }}>♛</span>
                </div>
                <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: '14px', color: '#0f172a' }}>IMPERIA AI</span>
              </Link>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
              {STEPS.map((s, i) => (
                <React.Fragment key={s.n}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0, transition: 'all 0.3s',
                      background: step > s.n ? '#D4A017' : step === s.n ? '#0f172a' : '#f1f5f9',
                      color: step > s.n ? '#0f172a' : step === s.n ? '#D4A017' : '#94a3b8',
                    }}>
                      {step > s.n ? '✓' : s.n}
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: step === s.n ? 600 : 400, color: step === s.n ? '#0f172a' : '#94a3b8', whiteSpace: 'nowrap' }} className="hidden sm:block">
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div style={{ height: '1px', flex: 1, minWidth: '12px', background: step > s.n ? '#D4A017' : '#e2e8f0', transition: 'background 0.3s' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="onboard-form-area" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '32px', maxWidth: '580px', margin: '0 auto', width: '100%' }}>

          {/* ── Stap 5: Analyse ── */}
          {step === 5 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', border: '3px solid rgba(212,160,23,0.2)', borderTopColor: '#D4A017', animation: 'spin 1.2s linear infinite', margin: '0 auto 24px' }} />
              <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
                Uw groeiscan loopt...
              </h2>
              <p style={{ color: '#64748b', marginBottom: '32px', fontSize: '15px' }}>Even geduld — wij analyseren uw bedrijf grondig.</p>

              {/* Progress */}
              <div style={{ background: '#e2e8f0', borderRadius: '100px', height: '8px', marginBottom: '24px', overflow: 'hidden' }}>
                <motion.div animate={{ width: `${scanProgress}%` }} transition={{ duration: 0.6 }}
                  style={{ height: '100%', background: 'linear-gradient(90deg, #D4A017, #f5c842)', borderRadius: '100px' }} />
              </div>
              <p data-testid="onboarding-scan-progress" role="status" style={{ color: '#D4A017', fontWeight: 600, fontSize: '14px', marginBottom: '24px' }}>{scanProgress}%</p>

              {/* Steps done */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'left' }}>
                {SCAN_STEPS.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <motion.div key={s.text}
                      animate={{ opacity: i <= scanStep ? 1 : 0.3, x: i === scanStep ? 0 : 0 }}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderRadius: '10px', background: i < scanStep ? 'rgba(34,197,94,0.08)' : i === scanStep ? 'rgba(212,160,23,0.08)' : 'transparent' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: i < scanStep ? '#22c55e' : i === scanStep ? '#D4A017' : '#e2e8f0', flexShrink: 0 }}>
                        {i < scanStep ? <CheckCircle size={14} color="white" /> : <Icon size={13} color={i === scanStep ? '#0f172a' : '#94a3b8'} />}
                      </div>
                      <span style={{ fontSize: '14px', color: i < scanStep ? '#22c55e' : i === scanStep ? '#0f172a' : '#94a3b8', fontWeight: i === scanStep ? 600 : 400 }}>
                        {s.text} {i < scanStep ? '✓' : i === scanStep ? '...' : ''}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Stap 6: Concurrenten bevestiging ── */}
          {step === 6 && (
            <div data-testid="concurrenten-bevestiging">
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #D4A017, #f5c842)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <CheckCircle size={28} color="#0f172a" />
                </div>
                <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
                  Analyse compleet!
                </h2>
                <p style={{ color: '#64748b', fontSize: '15px' }}>
                  We hebben ook uw directe concurrenten gevonden. Klopt dit?
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                {foundConcurrenten.map((c, i) => (
                  <div key={i} style={{
                    background: 'white', borderRadius: '14px', padding: '16px',
                    border: '1px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', gap: '14px',
                  }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '10px',
                      background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: "'Sora', sans-serif", fontSize: '14px', fontWeight: 800, color: '#D4A017', flexShrink: 0,
                    }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <input
                        type="text"
                        value={c.naam}
                        data-testid={`confirm-concurrent-naam-${i}`}
                        onChange={e => {
                          const arr = [...foundConcurrenten];
                          arr[i] = { ...arr[i], naam: e.target.value };
                          setFoundConcurrenten(arr);
                        }}
                        style={{
                          width: '100%', fontSize: '15px', fontWeight: 700, color: '#0f172a', border: 'none',
                          borderBottom: '1px solid #f1f5f9', padding: '4px 0', marginBottom: '4px',
                          fontFamily: "'Sora', sans-serif", background: 'transparent', outline: 'none',
                        }}
                      />
                      <input
                        type="text"
                        value={c.website}
                        data-testid={`confirm-concurrent-url-${i}`}
                        onChange={e => {
                          const arr = [...foundConcurrenten];
                          arr[i] = { ...arr[i], website: e.target.value };
                          setFoundConcurrenten(arr);
                        }}
                        placeholder="www.concurrent.nl"
                        style={{
                          width: '100%', fontSize: '12px', color: '#3b82f6', border: 'none',
                          padding: '4px 0', marginBottom: '4px', background: 'transparent', outline: 'none',
                        }}
                      />
                      <p style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.5 }}>
                        {c.waarom_concurrent || c.beschrijving}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: 'rgba(212,160,23,0.06)', borderRadius: '12px', padding: '14px', border: '1px solid rgba(212,160,23,0.15)', marginBottom: '24px' }}>
                <p style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.6 }}>
                  <strong style={{ color: '#D4A017' }}>Pas het aan:</strong> U kunt de namen en websites hierboven direct bewerken als ze niet kloppen. In uw dashboard kunt u concurrenten altijd wijzigen.
                </p>
              </div>

              <button
                data-testid="confirm-concurrenten-btn"
                onClick={confirmConcurrenten}
                className="rc-btn rc-btn-primary"
                style={{ width: '100%', borderRadius: '14px', border: 'none', fontSize: '16px', cursor: 'pointer', padding: '16px' }}>
                Bevestig & ga naar dashboard <ArrowRight size={18} />
              </button>
            </div>
          )}

          {step < 5 && step !== 6 && (
            <AnimatePresence mode="wait">
              <motion.div key={step} variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }}>

                {/* ── Stap 1: Website ── */}
                {step === 1 && (
                  <div>
                    <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '26px', fontWeight: 800, color: '#0f172a', marginBottom: '8px', letterSpacing: '-0.02em' }}>
                      Uw website en bedrijfsnaam
                    </h2>
                    <p style={{ color: '#64748b', marginBottom: '28px', fontSize: '15px' }}>
                      We analyseren uw website gratis — geen toegang vereist.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Bedrijfsnaam *</label>
                        <input data-testid="onboarding-company-name" type="text" value={form.company_name} onChange={e => set('company_name', e.target.value)}
                          placeholder="Bijv. Bakkerij De Hoekstee" className="rc-input" style={{ background: 'white' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Website URL *</label>
                        <div style={{ position: 'relative' }}>
                          <Globe style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '17px', height: '17px', color: '#94a3b8' }} />
                          <input data-testid="onboarding-website-url" type="text" value={form.website_url} onChange={e => set('website_url', e.target.value)}
                            placeholder="www.uwbedrijf.nl" className="rc-input"
                            style={{ background: 'white', paddingLeft: '42px', paddingRight: urlStatus ? '42px' : '16px' }} />
                          {urlChecking && (
                            <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', border: '2px solid #D4A017', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                          )}
                          {urlStatus === 'ok' && !urlChecking && (
                            <CheckCircle style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', width: '17px', height: '17px', color: '#22c55e' }} />
                          )}
                          {urlStatus === 'fail' && !urlChecking && (
                            <X style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', width: '17px', height: '17px', color: '#ef4444' }} />
                          )}
                        </div>
                        {form.website_url && (
                          <p style={{ fontSize: '11px', color: urlStatus === 'ok' ? '#22c55e' : urlStatus === 'fail' ? '#ef4444' : '#94a3b8', marginTop: '4px' }}>
                            {urlChecking ? 'Website controleren...' : urlStatus === 'ok' ? '✓ Website gevonden — klaar voor analyse' : urlStatus === 'fail' ? '✗ Website niet gevonden — controleer de URL' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Stap 2: Branche ── */}
                {step === 2 && (
                  <div>
                    <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '26px', fontWeight: 800, color: '#0f172a', marginBottom: '8px', letterSpacing: '-0.02em' }}>
                      In welke branche bent u actief?
                    </h2>
                    <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '15px' }}>Kies de branche die het best bij uw bedrijf past.</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px' }}>
                      {BRANCHES.map(b => (
                        <button data-testid={`onboarding-industry-${b.id}`} key={b.id} type="button" onClick={() => { set('branche', b.id); set('branche_label', b.label); }}
                          style={{ padding: '16px 12px', borderRadius: '14px', border: `2px solid ${form.branche === b.id ? '#D4A017' : '#e2e8f0'}`, background: form.branche === b.id ? '#D4A017' : 'white', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center' }}>
                          <div style={{ fontSize: '24px', marginBottom: '6px' }}>{b.icon}</div>
                          <p style={{ fontSize: '12px', fontWeight: 600, color: form.branche === b.id ? '#0f172a' : '#374151', lineHeight: 1.3 }}>{b.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Stap 3: Locatie & Servicebereik ── */}
                {step === 3 && (
                  <div>
                    <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '26px', fontWeight: 800, color: '#0f172a', marginBottom: '8px', letterSpacing: '-0.02em' }}>
                      Locatie & servicebereik
                    </h2>
                    <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '15px' }}>
                      Dit bepaalt hoe we uw lokale zichtbaarheid en bereikbaarheid analyseren.
                    </p>

                    {/* Vestigingsplaats */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Vestigingsplaats *</label>
                      <div style={{ position: 'relative' }}>
                        <MapPin style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '17px', height: '17px', color: '#94a3b8' }} />
                        <input data-testid="onboarding-location" type="text" value={form.locatie} onChange={e => set('locatie', e.target.value)}
                          placeholder="Bijv. Zwolle, Amsterdam" className="rc-input"
                          style={{ background: 'white', paddingLeft: '42px' }} />
                      </div>
                      <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {['Amsterdam', 'Rotterdam', 'Den Haag', 'Utrecht', 'Eindhoven', 'Zwolle', 'Groningen', 'Tilburg'].map(city => (
                          <button data-testid={`onboarding-city-${city.toLowerCase()}`} key={city} type="button" onClick={() => set('locatie', city)}
                            style={{ padding: '6px 12px', borderRadius: '20px', border: `1px solid ${form.locatie === city ? '#D4A017' : '#e2e8f0'}`, background: form.locatie === city ? '#D4A017' : 'white', fontSize: '12px', fontWeight: 500, color: form.locatie === city ? '#0f172a' : '#374151', cursor: 'pointer', transition: 'all 0.2s' }}>
                            {city}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Servicebereik */}
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '10px' }}>Hoe groot is uw servicebereik? *</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        {[
                          { id: 'lokaal', label: 'Lokaal', desc: 'Binnen één stad of gemeente', icon: '📍' },
                          { id: 'regionaal', label: 'Regionaal', desc: 'Meerdere steden of provincie', icon: '🗺️' },
                          { id: 'nationaal', label: 'Nationaal', desc: 'Heel Nederland', icon: '🇳🇱' },
                          { id: 'internationaal', label: 'Internationaal', desc: 'Ook buiten Nederland', icon: '🌍' },
                        ].map(opt => (
                          <button data-testid={`onboarding-service-${opt.id}`} key={opt.id} type="button" onClick={() => set('service_type', opt.id)}
                            style={{ padding: '14px', borderRadius: '12px', border: `2px solid ${form.service_type === opt.id ? '#D4A017' : '#e2e8f0'}`, background: form.service_type === opt.id ? '#D4A017' : 'white', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left' }}>
                            <div style={{ fontSize: '20px', marginBottom: '6px' }}>{opt.icon}</div>
                            <p style={{ fontWeight: 700, fontSize: '13px', color: form.service_type === opt.id ? '#0f172a' : '#374151', marginBottom: '2px' }}>{opt.label}</p>
                            <p style={{ fontSize: '11px', color: form.service_type === opt.id ? 'rgba(0,0,0,0.6)' : '#94a3b8' }}>{opt.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Stap 4: Doelen ── */}
                {step === 4 && (
                  <div>
                    <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '26px', fontWeight: 800, color: '#0f172a', marginBottom: '8px', letterSpacing: '-0.02em' }}>
                      Wat wilt u het liefst bereiken?
                    </h2>
                    <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '15px' }}>
                      Kies één of meerdere doelen. We prioriteren de analyse op basis van uw keuze.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      {DOELEN.map(({ id, label, icon: Icon, color }) => {
                        const selected = form.groeidoelen.includes(id);
                        return (
                          <button data-testid={`onboarding-goal-${id}`} key={id} type="button" onClick={() => toggleDoel(id)}
                            style={{ padding: '16px', borderRadius: '14px', border: `2px solid ${selected ? color : '#e2e8f0'}`, background: selected ? `${color}10` : 'white', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: selected ? `${color}20` : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Icon style={{ width: '18px', height: '18px', color: selected ? color : '#94a3b8' }} />
                            </div>
                            <span style={{ fontSize: '13px', fontWeight: selected ? 700 : 500, color: selected ? '#0f172a' : '#374151', lineHeight: 1.3 }}>{label}</span>
                            {selected && <CheckCircle style={{ width: '16px', height: '16px', color, marginLeft: 'auto', flexShrink: 0 }} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px', gap: '12px' }}>
                  {step > 1 ? (
                    <button data-testid="onboarding-back" onClick={() => setStep(s => s - 1)}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '12px', border: '2px solid #e2e8f0', background: 'white', fontWeight: 600, fontSize: '14px', color: '#374151', cursor: 'pointer', transition: 'all 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = '#D4A017'} onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
                      <ArrowLeft size={16} /> Terug
                    </button>
                  ) : (
                    <Link to="/register" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '12px', border: '2px solid #e2e8f0', background: 'white', fontWeight: 600, fontSize: '14px', color: '#374151', textDecoration: 'none' }}>
                      <ArrowLeft size={16} /> Terug
                    </Link>
                  )}

                  {step < 4 ? (
                    <button data-testid="onboarding-next" onClick={() => setStep(s => s + 1)} disabled={!canNext()}
                      className="rc-btn rc-btn-primary"
                      style={{ borderRadius: '14px', opacity: canNext() ? 1 : 0.5, border: 'none', fontSize: '15px', cursor: canNext() ? 'pointer' : 'not-allowed' }}>
                      Volgende <ArrowRight size={17} />
                    </button>
                  ) : (
                    <button data-testid="onboarding-start-analysis" onClick={startAnalyse} disabled={!canNext()}
                      className="rc-btn rc-btn-primary"
                      style={{ borderRadius: '14px', opacity: canNext() ? 1 : 0.5, border: 'none', fontSize: '15px', cursor: canNext() ? 'pointer' : 'not-allowed' }}>
                      Start analyse <Zap size={17} />
                    </button>
                  )}
                </div>

                <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '12px', marginTop: '16px' }}>
                  Stap {step} van 4 · Duurt minder dan 2 minuten
                </p>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
}
